import { useEffect, useMemo } from "react";
import { userType } from "../constants/user";
import { create } from "zustand";
import { User } from "../types/user";
import { AppUserSession } from "../services/user-session";
import { useGetUserProfile } from "../services/api/user.api";
import { validateToken } from "../utils";

interface CurrentTalentState {
  user: User | null;
  setUser: (user: User | null) => void;
  authToken: null | string;
  setAuthToken: (token: string | null) => void;
  resetUser: () => void;
}

const useAuthStore = create<CurrentTalentState>()((set) => ({
  user: null,
  setUser: (user) =>
    set(() => ({
      user,
    })),
  authToken: null,
  setAuthToken: (token) =>
    set(() => ({
      authToken: token,
    })),
  resetUser: () => set(() => ({ user: null })),
}));

export function useAuth() {
  const { user, setUser, authToken, setAuthToken, resetUser } = useAuthStore();
  const tokenInSession = AppUserSession.getToken();

  useEffect(() => {
    if (!authToken && tokenInSession) {
      setAuthToken(tokenInSession);
    }
  }, [authToken, tokenInSession, setAuthToken]);

  const effectiveToken = authToken ?? tokenInSession ?? null;

  const shouldBootstrapProfile = useMemo(
    () =>
      !!effectiveToken && !user && validateToken(effectiveToken),
    [effectiveToken, user],
  );

  const { data: profileResponse, isFetching: profileBootstrapping } =
    useGetUserProfile({
      key: ["user-profile"],
      options: {
        enabled: shouldBootstrapProfile,
      },
    });

  useEffect(() => {
    if (profileResponse?.data) {
      setUser(profileResponse.data as User);
    }
  }, [profileResponse, setUser]);

  return {
    accessToken: effectiveToken,
    userInfo: user,
    /** True while we have a valid token in storage but user is not yet hydrated (avoids flashing guest routes after refresh). */
    authBootstrapping: shouldBootstrapProfile && profileBootstrapping,
    isUserLogin: !!effectiveToken && user?.account_type === userType.USER,
    isTalentLogin: !!effectiveToken && user?.account_type === userType.TALENT,
    isBusinessLogin:
      !!effectiveToken && user?.account_type === userType.BUSINESS,
    setUser,
    setAuthToken,
    resetUser,
  };
}
